import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dropdown, DropdownButton, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Select from 'react-select';
import { AuthContext } from '../contexts/AuthContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Table, Form, Container, Button, Modal } from 'react-bootstrap';
import { FaEdit, FaTrash, FaDatabase } from 'react-icons/fa';
import { Clipboard, Calculator } from 'react-bootstrap-icons';
import BulkEditModal from '../components/BulkEditModal';
import './CRTDatabase.css'

const STORE_API_URL = process.env.REACT_APP_API_URL;

function CRTAdmin() {
  const { token } = useContext(AuthContext);
  const [crtData, setCrtData] = useState([]);
  const [attributes, setAttributes] = useState({});

  // Filtering options
  const [selectedBrand, setSelectedBrand] = useState(localStorage.getItem('selectedBrand') || 'All');
  const [selectedChassis, setSelectedChassis] = useState(localStorage.getItem('selectedChassis') || 'All');
  const [selectedStatus, setSelectedStatus] = useState(localStorage.getItem('selectedStatus') || 'All');
  const [chassisOptions, setChassisOptions] = useState([]);
  
  // Sorting options for the table
  const [sortColumn, setSortColumn] = useState('brand');
  const [sortOrder, setSortOrder] = useState('asc');

  // Delete variables
  const [deletingId, setDeletingId] = useState(null);
  const [deletePressTimer, setDeletePressTimer] = useState(null);
  
  // Bulk edit variables
  const [selectedCRTs, setSelectedCRTs] = useState([]);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  
  // Column visibility
  const [showColumnModal, setShowColumnModal] = useState(false);
  const defaultVisibleColumns = ['title', 'chassis', 'mod_boards', 'rgb_modifiable', 'component_modifiable', 'svideo_modifiable', 'published', 'interesting', 'actions'];
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('crtTableVisibleColumns');
    return saved ? JSON.parse(saved) : defaultVisibleColumns;
  });

  const statusOptions = [
    { value: 'All', label: 'All' },
    { value: 'new', label: 'New' },
    { value: 'contribution', label: 'Contributions' },
  ];
  
  const navigate = useNavigate();

  // Available columns definition
  const availableColumns = [
    { key: 'title', label: 'Title', sortable: true },
    { key: 'brand', label: 'Brand', sortable: true },
    { key: 'model', label: 'Model', sortable: true },
    { key: 'launched', label: 'Launched', sortable: true },
    { key: 'chassis', label: 'Chassis', sortable: true },
    { key: 'series', label: 'Series', sortable: true },
    { key: 'chroma', label: 'Chroma', sortable: true },
    { key: 'mod_boards', label: 'Mod Boards', sortable: true },
    { key: 'mods', label: 'Mods', sortable: true },
    { key: 'rgb_modifiable', label: 'RGB', sortable: true },
    { key: 'component_modifiable', label: 'Component', sortable: true },
    { key: 'svideo_modifiable', label: 'S-Video', sortable: true },
    { key: 'composite_modifiable', label: 'Composite', sortable: true },
    { key: 'viewable_size', label: 'Size', sortable: true },
    { key: 'tube', label: 'Tube', sortable: true },
    { key: 'power', label: 'Power', sortable: true },
    { key: 'weight', label: 'Weight', sortable: true },
    { key: 'published', label: 'Published', sortable: true },
    { key: 'interesting', label: 'Interesting', sortable: true },
    { key: 'actions', label: 'Actions', sortable: false }
  ];

  // Handle column visibility changes
  const handleColumnVisibilityChange = (columnKey, isVisible) => {
    const newVisibleColumns = isVisible 
      ? [...visibleColumns, columnKey]
      : visibleColumns.filter(col => col !== columnKey);
    
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('crtTableVisibleColumns', JSON.stringify(newVisibleColumns));
  };

  // Reset to default columns
  const resetToDefaultColumns = () => {
    setVisibleColumns(defaultVisibleColumns);
    localStorage.setItem('crtTableVisibleColumns', JSON.stringify(defaultVisibleColumns));
  };

  // Check if column is visible
  const isColumnVisible = (columnKey) => {
    return visibleColumns.includes(columnKey);
  };

  // Render table cell based on column type
  const renderTableCell = (crt, columnKey) => {
    switch (columnKey) {
      case 'title':
        return (
          <td key={columnKey}>
            {crt.slug ? (
              <a
                href={`/crts/${crt.slug}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {crt.title}
              </a>
            ) : (
              crt.title
            )}
          </td>
        );
      
      case 'brand':
        return <td key={columnKey}>{crt.brand}</td>;
      
      case 'model':
        return <td key={columnKey}>{crt.model}</td>;
      
      case 'launched':
        return <td key={columnKey}>{crt.launched}</td>;
      
      case 'chassis':
        return <td key={columnKey}>{crt.chassis}</td>;
      
      case 'mod_boards':
        return (
          <td key={columnKey}>
            {crt.mod_boards && crt.mod_boards.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px' }}>
                {crt.mod_boards.map((board, idx) => (
                  <span 
                    key={idx} 
                    className="badge bg-secondary"
                    style={{ fontSize: '0.7rem' }}
                  >
                    {typeof board === 'string' ? board : board.name}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-muted">-</span>
            )}
          </td>
        );
      
      case 'mods':
        return <td key={columnKey}>{crt.mods}</td>;
      
      case 'viewable_size':
        return <td key={columnKey}>{crt.viewable_size}</td>;
      
      case 'tube':
        // Handle both old and new tube format
        if (crt.tubes && Array.isArray(crt.tubes) && crt.tubes.length > 0) {
          return (
            <td key={columnKey}>
              {crt.tubes.map((tube, idx) => (
                <div key={idx} style={{ fontSize: '0.85em' }}>
                  {tube.model} {tube.brand && `(${tube.brand})`}
                </div>
              ))}
            </td>
          );
        }
        // Fallback to old format
        return <td key={columnKey}>{Array.isArray(crt.tube) ? crt.tube.join(', ') : crt.tube}</td>;
      
      case 'power':
        return <td key={columnKey}>{crt.power}</td>;
      
      case 'weight':
        return <td key={columnKey}>{crt.weight}</td>;
      
      case 'published':
        return (
          <td key={columnKey}>
            <Form.Check
              type="checkbox"
              checked={crt.published || false}
              onChange={(e) => handleBooleanChange(crt._id, 'published', e.target.checked)}
              label=""
            />
          </td>
        );

      case 'interesting':
        return (
          <td key={columnKey}>
            <Form.Check
              type="checkbox"
              checked={crt.interesting || false}
              onChange={(e) => handleBooleanChange(crt._id, 'interesting', e.target.checked)}
              label=""
            />
          </td>
        );
      
      case 'rgb_modifiable':
        return (
          <td key={columnKey} className="d-flex align-items-center gap-2">
            <DropdownButton
              id={`rgb-mod-${crt._id}`}
              title={crt.rgb_modifiable || 'Unknown'}
              variant="outline-secondary"
              size="sm"
              onSelect={(value) => handleModChange(crt._id, 'rgb_modifiable', value)}
            >
              <Dropdown.Item eventKey="Yes">Yes</Dropdown.Item>
              <Dropdown.Item eventKey="No">No</Dropdown.Item>
              <Dropdown.Item eventKey="Unknown">Unknown</Dropdown.Item>
            </DropdownButton>

            {crt.rgb_mod_notes?.trim() && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id={`tooltip-${crt._id}`}>
                    {crt.rgb_mod_notes.length > 250
                      ? `${crt.rgb_mod_notes.slice(0, 250)}…`
                      : crt.rgb_mod_notes}
                  </Tooltip>
                }
              >
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    navigator.clipboard.writeText(crt.rgb_mod_notes);
                    toast.success('Copied RGB notes!');
                  }}
                >
                  <Clipboard size={14} />
                </button>
              </OverlayTrigger>
            )}

            {crt.rgb_mux_calculator_enabled === 'true' && crt.slug && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id={`calc-tooltip-${crt._id}`}>
                    Open RGB MUX Calculator
                  </Tooltip>
                }
              >
                <a
                  href={`/rgb-mux-calculator?crt_slug=${crt.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-secondary btn-sm"
                >
                  <Calculator size={14} />
                </a>
              </OverlayTrigger>
            )}
          </td>
        );
      
      case 'component_modifiable':
        return (
          <td key={columnKey}>
            <DropdownButton 
              id={`component-mod-${crt._id}`} 
              title={crt.component_modifiable || 'Unknown'} 
              variant="outline-secondary" 
              size="sm"
              onSelect={(value) => handleModChange(crt._id, 'component_modifiable', value)}
            >
              <Dropdown.Item eventKey="Yes">Yes</Dropdown.Item>
              <Dropdown.Item eventKey="No">No</Dropdown.Item>
              <Dropdown.Item eventKey="Unknown">Unknown</Dropdown.Item>
            </DropdownButton>
          </td>
        );
      
      case 'svideo_modifiable':
        return (
          <td key={columnKey}>
            <DropdownButton 
              id={`svideo-mod-${crt._id}`} 
              title={crt.svideo_modifiable || 'Unknown'} 
              variant="outline-secondary" 
              size="sm"
              onSelect={(value) => handleModChange(crt._id, 'svideo_modifiable', value)}
            >
              <Dropdown.Item eventKey="Yes">Yes</Dropdown.Item>
              <Dropdown.Item eventKey="No">No</Dropdown.Item>
              <Dropdown.Item eventKey="Unknown">Unknown</Dropdown.Item>
            </DropdownButton>
          </td>
        );
      
      case 'composite_modifiable':
        return (
          <td key={columnKey}>
            <DropdownButton 
              id={`composite-mod-${crt._id}`} 
              title={crt.composite_modifiable || 'Unknown'} 
              variant="outline-secondary" 
              size="sm"
              onSelect={(value) => handleModChange(crt._id, 'composite_modifiable', value)}
            >
              <Dropdown.Item eventKey="Yes">Yes</Dropdown.Item>
              <Dropdown.Item eventKey="No">No</Dropdown.Item>
              <Dropdown.Item eventKey="Unknown">Unknown</Dropdown.Item>
            </DropdownButton>
          </td>
        );
      
      case 'actions':
        return (
          <td key={columnKey} style={{ whiteSpace: 'nowrap' }}>
            <button
              className="btn btn-secondary btn-sm me-1"
              onClick={() => handleCloneCRT(crt._id)}
            >
              Clone
            </button>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <button
                className="btn btn-danger btn-sm me-1"
                onMouseDown={() => {
                  setDeletingId(crt._id);
                  const timer = setTimeout(() => {
                    handleDeleteCRT(crt._id);
                    setDeletingId(null);
                  }, 3000);
                  setDeletePressTimer(timer);
                }}
                onMouseUp={() => {
                  clearTimeout(deletePressTimer);
                  setDeletingId(null);
                }}
                onMouseLeave={() => {
                  clearTimeout(deletePressTimer);
                  setDeletingId(null);
                }}
                title="Long press to delete"
              >
                Delete
              </button>
              {deletingId === crt._id && (
                <div className="hold-progress-bar" />
              )}
            </div>
            <Link to={`/superadmin/edit-crt/${crt._id}`} className="btn btn-warning btn-sm">
              Edit
            </Link>
          </td>
        );
      
      default:
        return <td key={columnKey}>{crt[columnKey]}</td>;
    }
  };

  const selectStyles = {
    container: (provided) => ({
      ...provided,
      minWidth: '150px',
    }),
    control: (provided, state) => ({
      ...provided,
      backgroundColor: 'var(--bs-secondary-bg)', // matches button background
      borderColor: 'var(--bs-border-color)',
      color: 'var(--text-color)',
      borderRadius: '0.375rem',
      padding: '0 0.25rem',
      minHeight: 'calc(1.5em + 0.75rem + 2px)',
      boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(255,76,76,0.25)' : 'none',
      '&:hover': {
        borderColor: '#ff4c4c',
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'var(--text-color-light)',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'var(--bg-color-float)',
      border: `1px solid var(--bs-border-color)`,
      borderRadius: '0.375rem',
      zIndex: 5,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused
        ? '#ff4c4c'
        : 'transparent',
      color: state.isFocused ? '#fff' : 'var(--text-color)',
      cursor: 'pointer',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: 'var(--text-color-light)',
      padding: '0 4px',
    }),
    input: (provided) => ({
      ...provided,
      color: 'var(--text-color-light)',
    }),
  };  

  useEffect(() => {
    if (!token) return;
    fetchCrtData(selectedBrand, selectedChassis, selectedStatus);
    if (selectedBrand && selectedBrand !== 'All') {
      fetchChassisValues(selectedBrand);
    }
    fetchAttributes();
  }, [token, selectedStatus]);

  const fetchAttributes = async () => {
    try {
      const response = await fetch(`${STORE_API_URL}/crtattributes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
  
      // If we're going to override chassis via fetchChassisValues, skip it here
      const filteredData =
        selectedBrand && selectedBrand !== 'All'
          ? { ...data, chassis: attributes.chassis } // retain existing filtered chassis
          : data;
  
      setAttributes(filteredData);
    } catch (error) {
      toast.error('Failed to load CRT attributes.');
    }
  };

  const fetchCrtData = async (brand = '', chassis = '', status = 'All') => {
    if (!token) return;
  
    try {
      let url = `${STORE_API_URL}/crts`;
      const queryParams = [];
      
      if (brand && brand !== 'All') queryParams.push(`brand=${encodeURIComponent(brand)}`);
      if (chassis && chassis !== 'All') queryParams.push(`chassis=${encodeURIComponent(chassis)}`);
      if (status && status !== 'All') {
        if (status === 'none') {
          queryParams.push('status=null');
        } else {
          queryParams.push(`status=${encodeURIComponent(status)}`);
        }
      }
  
      if (queryParams.length > 0) {
        url += `?${queryParams.join('&')}`;
      }
  
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
  
      if (!response.ok) {
        throw new Error(`Failed to fetch CRTs: ${response.statusText}`);
      }
  
      const data = await response.json();
      setCrtData(data.crts);
      setSelectedCRTs([]);
      setSelectAll(false);
    } catch (error) {
      toast.error(error.message || 'Failed to load CRT data.');
    }
  };  

  const fetchChassisValues = async (brand) => {
    try {
      const res = await fetch(`${STORE_API_URL}/crtattributes/chassis?brand=${encodeURIComponent(brand)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setChassisOptions(data.values || []);
    } catch (err) {
      toast.error('Failed to load chassis options');
    }
  };

  const handleBrandChange = (brand) => {
    setSelectedBrand(brand);
    localStorage.setItem('selectedBrand', brand);
    fetchChassisValues(brand);
    fetchCrtData(brand, 'All', selectedStatus);
  };

  const handleChassisChange = (chassis) => {
    setSelectedChassis(chassis);
    localStorage.setItem('selectedChassis', chassis);
    fetchCrtData(selectedBrand, chassis, selectedStatus);
  };

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    localStorage.setItem('selectedStatus', status);
    fetchCrtData(selectedBrand, selectedChassis, status);
  };

  const handleSort = (column) => {
    const newOrder = sortColumn === column && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortColumn(column);
    setSortOrder(newOrder);
  
    // Sort the data
    const sortedData = [...crtData].sort((a, b) => {
      let valA, valB;
      
      // Special handling for mod_boards array
      if (column === 'mod_boards') {
        valA = a[column] && a[column].length > 0 
          ? a[column].map(board => typeof board === 'string' ? board : board.name).join(', ').toLowerCase()
          : '';
        valB = b[column] && b[column].length > 0 
          ? b[column].map(board => typeof board === 'string' ? board : board.name).join(', ').toLowerCase()
          : '';
      } else {
        valA = a[column] ? a[column].toString().toLowerCase() : ''; 
        valB = b[column] ? b[column].toString().toLowerCase() : ''; 
      }
  
      if (valA < valB) return newOrder === 'asc' ? -1 : 1;
      if (valA > valB) return newOrder === 'asc' ? 1 : -1;
      return 0; // Equal values remain unchanged
    });
  
    setCrtData(sortedData);
  };

  const handleModChange = async (crtId, field, value) => {
    if (!token) return;
  
    try {
      const response = await fetch(`${STORE_API_URL}/crts/${crtId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ [field]: value === 'Unknown' ? '' : value })
      });
  
      if (!response.ok) {
        throw new Error(`Failed to update ${field}: ${response.statusText}`);
      }
  
      // Update local state to reflect change
      setCrtData(prevCrtData =>
        prevCrtData.map(crt => crt._id === crtId ? { ...crt, [field]: value } : crt)
      );
  
      toast.success(`${field.replace('_', ' ')} updated successfully.`);
    } catch (error) {
      toast.error(error.message || `Failed to update ${field}.`);
    }
  };

  const handleBooleanChange = async (crtId, field, value) => {
    if (!token) return;
  
    try {
      const response = await fetch(`${STORE_API_URL}/crts/${crtId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ [field]: value })
      });
  
      if (!response.ok) {
        throw new Error(`Failed to update ${field}: ${response.statusText}`);
      }
  
      // Update local state to reflect change
      setCrtData(prevCrtData =>
        prevCrtData.map(crt => crt._id === crtId ? { ...crt, [field]: value } : crt)
      );
  
      toast.success(`${field.replace(/_/g, ' ')} updated successfully.`);
    } catch (error) {
      toast.error(error.message || `Failed to update ${field}.`);
    }
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showAddOption, setShowAddOption] = useState(false);
  
  // Fetch suggestions
  const handleSearchChange = async (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (value.length < 2) return setSuggestions([]);

    try {
      const res = await fetch(`${STORE_API_URL}/crts/autocomplete?q=${encodeURIComponent(value)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setSuggestions(data.suggestions || []);

      const foundMatch = data.suggestions?.some(
        (s) => s.title?.toLowerCase() === value.toLowerCase()
      );
      
      setShowAddOption(!foundMatch);
    } catch (err) {
      console.error('Autocomplete error:', err);
    }
  };

  const handleSuggestionSelect = (crt) => {
    setSearchQuery(crt.title);
    setSuggestions([]);
    navigate(`/superadmin/edit-crt/${crt._id}`);
  };

  const handleCloneCRT = async (crtId) => {
    if (!token) return;
  
    if (window.confirm('Clone this CRT?')) {
      try {
        const res = await fetch(`${STORE_API_URL}/crts/${crtId}/clone`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
  
        if (res.ok) {
          toast.success('CRT cloned successfully');
          fetchCrtData(selectedBrand, selectedChassis, selectedStatus); // Refresh the list
        } else {
          throw new Error(data.error || 'Clone failed');
        }
      } catch (err) {
        toast.error(err.message);
      }
    }
  };

  const handleDeleteCRT = async (crtId) => {
    if (!token) return;
  
    try {
      const res = await fetch(`${STORE_API_URL}/crts/${crtId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
  
      if (res.ok) {
        toast.success('CRT deleted');
        fetchCrtData(selectedBrand, selectedChassis, selectedStatus);
      } else {
        throw new Error(data.error || 'Delete failed');
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Bulk selection handlers
  const handleSelectCRT = (crt, isSelected) => {
    if (isSelected) {
      setSelectedCRTs(prev => [...prev, crt]);
    } else {
      setSelectedCRTs(prev => prev.filter(item => item._id !== crt._id));
    }
  };

  const handleSelectAllCRTs = (isSelected) => {
    setSelectAll(isSelected);
    if (isSelected) {
      setSelectedCRTs([...crtData]);
    } else {
      setSelectedCRTs([]);
    }
  };

  const isCRTSelected = (crtId) => {
    return selectedCRTs.some(crt => crt._id === crtId);
  };
  
  return (
    <Container>
      <ToastContainer />
      <div className="headline d-flex justify-content-between align-items-center">
        <h2 className="underline">CRT Database Management</h2>
      </div>
      <div style={{ display: 'inline-block' }}>
        <Link to="/superadmin/add-crt">
          <h6>Add CRT</h6>
        </Link>
      </div>

      <div className="d-flex justify-content-between align-items-center">
        <div className="me-2 mb-2 position-relative" style={{ minWidth: '400px' }}>
          <Form.Control
            type="text"
            placeholder="Search CRT by brand, model, or series..."
            value={searchQuery}
            onChange={handleSearchChange}
            autoComplete="off"
          />
          {(suggestions.length > 0 || showAddOption) && searchQuery.trim() !== '' && (
            <ul className="list-group position-absolute w-100">
              {suggestions.map((crt) => (
                <li
                  key={crt._id}
                  className="list-group-item list-group-item-action"
                  onClick={() => handleSuggestionSelect(crt)}
                >
                  {crt.title}
                </li>
              ))}
              {showAddOption && (
                <li
                  className="list-group-item list-group-item-action text-primary"
                  style={{ cursor: 'pointer', fontStyle: 'italic' }}
                  onClick={() => navigate(`/superadmin/add-crt?title=${encodeURIComponent(searchQuery)}`)}
                >
                  ➕ Add CRT: "{searchQuery}"
                </li>
              )}
            </ul>
          )}
        </div>
        <div className="filter-buttons mb-2">
            {selectedCRTs.length > 0 && (
              <Button 
                variant="primary" 
                onClick={() => setShowBulkEditModal(true)}
                className="me-2 bulk-edit-btn btn-sm"
              >
                Bulk Edit <span className="bulk-edit-count">{selectedCRTs.length}</span>
              </Button>
            )}

            <Button 
              variant="outline-secondary" 
              onClick={() => setShowColumnModal(true)}
              className="me-2 btn-sm"
            >
              <FaDatabase/>
            </Button>

          {/* Submission Type filter */}
          <Select
            className="me-2"
            styles={selectStyles}
            options={statusOptions}
            value={statusOptions.find(opt => opt.value === selectedStatus)}
            onChange={(option) => handleStatusChange(option.value)}
            placeholder="Filter by submission type..."
          />

          {/* Brand filter */}
          <Select
            className="me-2"
            styles={selectStyles}
            options={[{ value: 'All', label: 'All' }, ...(attributes.brand?.values || []).map(b => ({ value: b, label: b }))]}
            value={{ value: selectedBrand, label: selectedBrand }}
            onChange={(option) => handleBrandChange(option.value)}
            placeholder="Filter by brand..."
            isSearchable
          />

          {/* Chassis Filter */}
          <Select
            styles={selectStyles}
            options={[{ value: 'All', label: 'All' }, ...chassisOptions.map(c => ({ value: c, label: c }))]}
            value={{ value: selectedChassis, label: selectedChassis }}
            onChange={(option) => handleChassisChange(option.value)}
            placeholder="Filter by chassis..."
            isSearchable
          />
        </div>
      </div>
      <div className="mb-2 d-flex justify-content-between align-items-center">
        <div>
          <strong>Showing {crtData.length} CRT{crtData.length !== 1 ? 's' : ''}</strong>
        </div>
      </div>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>
              <Form.Check
                type="checkbox"
                checked={selectAll}
                onChange={(e) => handleSelectAllCRTs(e.target.checked)}
                label=""
              />
            </th>
            {availableColumns.map(column => {
              if (!isColumnVisible(column.key)) return null;
              
              return (
                <th 
                  key={column.key}
                  onClick={column.sortable ? () => handleSort(column.key) : undefined}
                  style={column.sortable ? { cursor: 'pointer' } : undefined}
                >
                  {column.label} {column.sortable && sortColumn === column.key ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {crtData.map((crt) => (
            <tr key={crt._id} className={isCRTSelected(crt._id) ? 'selected-row' : ''}>
              <td>
                <Form.Check
                  type="checkbox"
                  checked={isCRTSelected(crt._id)}
                  onChange={(e) => handleSelectCRT(crt, e.target.checked)}
                />
              </td>
              {availableColumns.map(column => {
                if (!isColumnVisible(column.key)) return null;
                return renderTableCell(crt, column.key);
              })}
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Bulk Edit Modal */}
      <BulkEditModal
        show={showBulkEditModal}
        onHide={() => setShowBulkEditModal(false)}
        selectedCRTs={selectedCRTs}
        attributes={attributes}
        onSuccess={() => {
          fetchCrtData(selectedBrand, selectedChassis, selectedStatus);
          setShowBulkEditModal(false);
        }}
      />

      {/* Column Visibility Modal */}
      <Modal show={showColumnModal} onHide={() => setShowColumnModal(false)} size="md">
        <Modal.Header closeButton>
          <Modal.Title>Select Columns to Display</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            {availableColumns.map(column => (
              <div key={column.key} className="col-md-6 mb-2">
                <Form.Check
                  type="checkbox"
                  id={`column-${column.key}`}
                  label={column.label}
                  checked={isColumnVisible(column.key)}
                  onChange={(e) => handleColumnVisibilityChange(column.key, e.target.checked)}
                />
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={resetToDefaultColumns}>
            Reset to Default
          </Button>
          <Button variant="secondary" onClick={() => setShowColumnModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default CRTAdmin;